import { impedirHerencia, permitirHerencia, SESION_CAMBIADA } from '../prod/runtime/sesion-pestanas';
/**
 * api.ts — Fetch wrapper for Kapi POS API
 *
 * Features:
 * - Automatically attaches JWT from session storage
 * - Returns typed ApiResponse<T> from @netcom/shared
 * - Handles 401 → redirects to login
 * - Handles network errors gracefully
 * - Supports query params, body, custom headers
 */

import type { ApiResponse } from '@netcom/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RequestOptions<TBody = unknown> {
  /** HTTP method (defaults to GET) */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Request body (auto-serialized to JSON) */
  body?: TBody;
  /** Query string parameters */
  params?: Record<string, string | number | boolean | undefined | null>;
  /** Additional headers */
  headers?: Record<string, string>;
  /** AbortSignal for request cancellation */
  signal?: AbortSignal;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Network request failed — check your connection');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export const SESSION_CHANGED = 'Esta pantalla quedó con una sesión anterior y la operación no se completó. Recarga la página para continuar.';
let pageUserId: string | null = null;
let sessionChanged = false;
let sessionGeneration = 0;

/** La identidad visible queda fija hasta un login explícito o una recarga completa. */
export function bindSessionUser(userId: string | null, login = false): void {
  if (!login && pageUserId && userId !== pageUserId) {
    sessionChanged = true;
    throw new ApiError(403, 'SesionCambiada', SESSION_CHANGED);
  }
  pageUserId = userId;
  if (!login && userId && localStorage.getItem('kapi:active_user') === null) localStorage.setItem('kapi:active_user', userId);
  if (login) {
    sessionGeneration++;
    sessionChanged = false;
    if (userId) localStorage.setItem('kapi:active_user', userId);
    else localStorage.removeItem('kapi:active_user');
  }
}
export function assertPageSession(): string | null {
  const active = localStorage.getItem('kapi:active_user');
  if (sessionChanged || (pageUserId && active !== pageUserId)) {
    sessionChanged = true;
    throw new ApiError(403, 'SesionCambiada', SESSION_CHANGED);
  }
  return pageUserId;
}

/** Una SPA conserva promesas al salir: ni sus datos ni un 401 pueden afectar al siguiente login. */
function captureResponseSession(): () => void {
  const generation = sessionGeneration;
  const user = pageUserId;
  const active = localStorage.getItem('kapi:active_user');
  return () => {
    if (generation !== sessionGeneration || user !== pageUserId || active !== localStorage.getItem('kapi:active_user')) {
      // No marcar sessionChanged: la sesión nueva puede ser válida y debe seguir funcionando.
      throw new ApiError(403, 'SesionCambiada', SESSION_CHANGED);
    }
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** sessionStorage key de la sucursal elegida (mismo valor que SELECTED_BRANCH_KEY de auth.tsx; sin importarlo para no crear ciclo). */
const SELECTED_BRANCH_STORAGE_KEY = 'kapi:selected_branch';

/** Sucursal elegida en esta sesión, o null (una sola sucursal → la resuelve el JWT en el api). */
function readSelectedBranch(): string | null {
  try {
    return sessionStorage.getItem(SELECTED_BRANCH_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Agrega `branchId` de la sucursal elegida a las llamadas `/api/*` que no lo traen.
 * Prod guarda la sucursal en la sesión del servidor al iniciar sesión; aquí la sesión es el JWT
 * (que solo la resuelve si el usuario tiene UNA sucursal), así que con varias sucursales cada
 * llamada sin `branchId` daba 400. Las rutas de `/api/auth/*` no llevan sucursal.
 */
export function withSelectedBranch(url: string): string {
  const pathOnly = url.replace(/^https?:\/\/[^/]+/, '');
  if (!pathOnly.startsWith('/api/') || pathOnly.startsWith('/api/auth/')) return url;
  if (/[?&]branchId=/.test(url)) return url;
  const branch = readSelectedBranch();
  if (!branch) return url;
  return `${url}${url.includes('?') ? '&' : '?'}branchId=${encodeURIComponent(branch)}`;
}

/** N-PL15 · #707: encabezado con la sucursal de esta ventana (prod:WebApp/Scripts/kapi/kapi-sucursal-ventana.js:28,122-152). */
export const SUCURSAL_VENTANA_HEADER = 'X-Kapi-Sucursal';

/**
 * Sucursal con la que se DIBUJÓ esta ventana. Prod la publica en el `<meta name="kapi-sucursal">` con el claim Branch al
 * dibujar la página (prod:WebApp/Views/Shared/_SucursalVentana.cshtml:14-23,33) y kapi-sucursal-ventana.js la lee UNA vez
 * al cargar (:22-25). Aquí se fija con la primera sucursal que tiene la ventana (al cargar la pestaña, o al elegirla en el
 * selector obligatorio de varias sucursales, cuando aún no había página dibujada) y ya NO sigue a la selección: un
 * `setBranch` dentro de la pestaña deja montada la página dibujada para la sucursal anterior, y esa discrepancia es la que
 * el servidor registra. Solo la cambian una recarga o `bindWindowBranch` (login nuevo, salir o sesión vencida; en prod
 * entrar siempre es una navegación completa, prod:WebApp/Controllers/AccountController.cs:618-654).
 */
let windowBranch: string | null = null;

/** Empieza una ventana nueva: la sucursal de la sesión que inicia (login) o ninguna (salir, sesión vencida). */
export function bindWindowBranch(branchId: string | null): void {
  windowBranch = branchId && branchId.trim() !== '' ? branchId : null;
}

/** La sucursal de esta ventana (ver `windowBranch`), o null si todavía no tiene. */
export function readWindowBranch(): string | null {
  if (!windowBranch) windowBranch = readSelectedBranch();
  return windowBranch;
}

function mismoOrigen(url: string): boolean {
  try {
    const pagina = new URL(window.location.href);
    return new URL(String(url), pagina).origin === pagina.origin;
  } catch {
    return false;
  }
}

/**
 * Agrega `X-Kapi-Sucursal` con la sucursal con la que se dibujó esta ventana (`readWindowBranch`, no la selección actual)
 * a una petición del MISMO origen que no lo traiga ya (si ya lo trae, p. ej. la sucursal dueña de algo guardado sin
 * internet, se respeta). A otros orígenes no se les agrega. El servidor lo compara con la sucursal de la sesión y, en
 * fase 1, solo registra las diferencias. Si algo falla, sale como hoy.
 */
export function withSucursalVentana(url: string, headers: Record<string, string>): Record<string, string> {
  try {
    if (!mismoOrigen(url)) return headers;
    const nombre = SUCURSAL_VENTANA_HEADER.toLowerCase();
    if (Object.keys(headers).some(k => k.toLowerCase() === nombre)) return headers;
    const branch = readWindowBranch();
    if (branch) headers[SUCURSAL_VENTANA_HEADER] = branch;
  } catch {
    /* la petición sale como hoy */
  }
  return headers;
}

/**
 * Encabezado para reenviar algo guardado sin internet con su sucursal DUEÑA, no con la de la ventana
 * (prod:WebApp/Scripts/App/offline/kapi-offline-net.js:176-182, reenvío «forzar» = sucursalDuena()). Misma regla de origen
 * que `withSucursalVentana`: a un API de otro origen no se manda (no está en `allowHeaders` de CORS).
 */
export function sucursalDuenaHeaders(path: string, branchId: string | null | undefined): Record<string, string> {
  try {
    if (!branchId || branchId.trim() === '' || !mismoOrigen((import.meta.env.VITE_API_URL ?? '') + path)) return {};
    return { [SUCURSAL_VENTANA_HEADER]: branchId };
  } catch {
    return {};
  }
}

/** Build URL with query string params, filtering out null/undefined */
export function buildUrl(path: string, params?: RequestOptions['params']): string {
  const base = (import.meta.env.VITE_API_URL ?? '') + path;
  if (!params) return withSelectedBranch(base);

  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      qs.set(key, String(value));
    }
  }

  const qsStr = qs.toString();
  return withSelectedBranch(qsStr ? `${base}${base.includes('?') ? '&' : '?'}${qsStr}` : base);
}

/** Retrieve current JWT from session/local storage */
export function getAccessToken(): string | null {
  // Cognito stores tokens under a well-known key pattern.
  // We store our resolved token under a simpler key on login.
  return sessionStorage.getItem('kapi:access_token')
    ?? null;
}

/**
 * ¿Es la pantalla de login? La de la SPA vieja (`/login`) y, desde la Ola 1, la de prod (`/sistema/inicio`, plan §1.8). Ahí
 * un 401 es la respuesta normal a unas credenciales malas y no se avisa `kapi:unauthorized`.
 */
export function esPaginaLogin(pathname: string = window.location.pathname): boolean {
  return pathname.startsWith('/login') || /^\/sistema\/inicio(\/|$)/i.test(pathname);
}

/**
 * Evento `kapi:otra-sucursal` (plan §1.8): el servidor respondió 409 `ErrorKind: 'OtraSucursal'`
 * (packages/api/src/middleware/sucursal-ventana.ts; prod kapi-sucursal-ventana.js:106-120). Lo escucha el candado de
 * ventana de los documentos /sistema (src/prod/runtime/sucursal-ventana.ts); la SPA vieja no lo escucha.
 */
export const EVENTO_OTRA_SUCURSAL = 'kapi:otra-sucursal';
function avisarOtraSucursal(status: number, cuerpo: unknown): void {
  if (status !== 409 || !cuerpo || typeof cuerpo !== 'object') return;
  const d = cuerpo as { ErrorKind?: unknown; sucursalSesion?: unknown; sucursalSesionNombre?: unknown };
  if (d.ErrorKind !== 'OtraSucursal') return;
  try {
    window.dispatchEvent(new CustomEvent(EVENTO_OTRA_SUCURSAL, {
      detail: { sucursalSesion: typeof d.sucursalSesion === 'string' ? d.sucursalSesion : null,
        sucursalSesionNombre: typeof d.sucursalSesionNombre === 'string' ? d.sucursalSesionNombre : null },
    }));
  } catch { /* sin aviso */ }
}

/**
 * Emit a custom event to signal that the session has expired.
 * The AuthProvider (or router) listens for this event and calls
 * React Router's navigate() — avoiding a full-page reload redirect loop.
 */
function redirectToLogin(): void {
  const returnTo = window.location.pathname + window.location.search;
  window.dispatchEvent(
    new CustomEvent('kapi:unauthorized', {
      detail: { returnTo },
    }),
  );
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

/**
 * Make an authenticated API request.
 * Throws ApiError on non-2xx responses, NetworkError on network failure.
 */
export async function apiFetch<TData, TBody = unknown>(
  path: string,
  options: RequestOptions<TBody> = {},
): Promise<ApiResponse<TData>> {
  const { method = 'GET', body, params, headers: extraHeaders, signal } = options;

  const owner = path.startsWith('/api/auth/') ? null : assertPageSession();
  const assertResponseSession = captureResponseSession();
  const url = buildUrl(path, params);
  const token = getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...extraHeaders,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (owner) headers['X-Kapi-User'] = owner;
  withSucursalVentana(url, headers);

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    assertResponseSession();
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err; // Propagate abort as-is
    }
    throw new NetworkError(err);
  }

  assertResponseSession();

  // 401 → redirect to login (unless already there)
  if (response.status === 401) {
    const error = await response.json().catch(() => null) as { message?: string } | null;
    assertResponseSession();
    if (!esPaginaLogin()) {
      redirectToLogin();
    }
    throw new ApiError(401, 'UNAUTHORIZED', error?.message ?? 'La sesión expiró. Inicia sesión de nuevo.');
  }

  // Parse response body
  let data: ApiResponse<TData>;
  try {
    data = await response.json() as ApiResponse<TData>;
  } catch (err) {
    assertResponseSession();
    // Un abort a media descarga del body (payloads grandes, p.ej.
    // /api/products/all) rechaza AQUÍ, no en fetch(): propagarlo como
    // AbortError para que los callers lo ignoren igual que un abort normal
    // (si no, se pinta como PARSE_ERROR fatal en la UI).
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    throw new ApiError(
      response.status,
      'PARSE_ERROR',
      `Failed to parse response from ${method} ${path}`,
    );
  }

  assertResponseSession();
  const renewed = response.headers.get('X-Kapi-Token');
  if (renewed && response.ok) storeAccessToken(renewed);

  // Non-2xx — throw ApiError with server error details
  if (!response.ok) {
    const errData = data as unknown as { message?: string; error?: unknown; code?: string; details?: unknown };
    if (errData?.code === 'SesionCambiada') sessionChanged = true;
    avisarOtraSucursal(response.status, errData);
    throw new ApiError(
      response.status,
      errData?.code ?? 'API_ERROR',
      // Varias rutas responden el error como {ok:false,error:'…'} (p. ej. cfdi.routes.ts egreso/REP); sin esto el
      // usuario veía «Request failed: 422» en lugar del motivo del servidor.
      errData?.message ?? (typeof errData?.error === 'string' && errData.error ? errData.error : undefined) ?? `Request failed: ${response.status} ${response.statusText}`,
      errData?.details,
    );
  }

  // Normaliza el envelope. El contrato canónico es {isSuccess,response,message}
  // (ApiResponse) y el front lee `.response`/`.isSuccess`, pero varios handlers
  // del API responden el envelope crudo de Hono {success,data} sin usar ok().
  // Para que CUALQUIER handler funcione con el front sin tocar 29 rutas, copiamos
  // data→response y success→isSuccess cuando el canónico falte. No rompe páginas
  // que ya leen `.response` ni las que leen `.data` (ambos quedan presentes).
  const env = data as unknown as Record<string, unknown>;
  if (env.response === undefined && env.data !== undefined) env.response = env.data;
  if (env.isSuccess === undefined && env.success !== undefined) env.isSuccess = env.success;

  // Forma "pelada": muchos handlers responden el payload directo sin envelope
  // (c.json(result), c.json(rows), c.json({items})). Como el cuerpo es 2xx
  // (los errores ya lanzaron arriba), envolvemos en el contrato canónico para
  // que `.response` y extractData() (que lee .data ?? .response) funcionen sin
  // tocar las ~560 rutas no canónicas. No afecta cuerpos que ya traen envelope
  // (isSuccess o success/data → ya normalizados arriba) ni fallos de negocio
  // con isSuccess:false en 200 (isSuccess queda definido).
  if (env.isSuccess === undefined && env.response === undefined) {
    return { isSuccess: true, response: data, message: '' } as unknown as ApiResponse<TData>;
  }

  return data;
}

// ---------------------------------------------------------------------------
// Convenience methods
// ---------------------------------------------------------------------------

/** GET /path?params */
export function apiGet<TData>(
  path: string,
  params?: RequestOptions['params'],
  options?: Omit<RequestOptions, 'method' | 'body' | 'params'>,
): Promise<ApiResponse<TData>> {
  return apiFetch<TData>(path, { ...options, method: 'GET', params });
}

/** POST /path with body */
export function apiPost<TData, TBody = unknown>(
  path: string,
  body?: TBody,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<ApiResponse<TData>> {
  return apiFetch<TData, TBody>(path, { ...options, method: 'POST', body });
}

/** PUT /path with body */
export function apiPut<TData, TBody = unknown>(
  path: string,
  body?: TBody,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<ApiResponse<TData>> {
  return apiFetch<TData, TBody>(path, { ...options, method: 'PUT', body });
}

/** PATCH /path with body */
export function apiPatch<TData, TBody = unknown>(
  path: string,
  body?: TBody,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<ApiResponse<TData>> {
  return apiFetch<TData, TBody>(path, { ...options, method: 'PATCH', body });
}

/** DELETE /path */
export function apiDelete<TData>(
  path: string,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<ApiResponse<TData>> {
  return apiFetch<TData>(path, { ...options, method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// File download helper (authenticated blob download)
// ---------------------------------------------------------------------------

/**
 * Download a file from an authenticated endpoint.
 *
 * Auth is header-based (Bearer JWT), so a plain anchor navigation would not
 * carry the token. This requests the endpoint with the Authorization header,
 * reads the response as a Blob, and triggers a browser download.
 *
 * @param path             API path (e.g. '/api/reports/export/excel')
 * @param params           Query string params
 * @param fallbackFilename Filename used if the server sends no Content-Disposition
 *
 * @throws ApiError on non-2xx (parses the JSON error body when present)
 * @throws NetworkError on network failure
 */
export async function downloadFile(
  path: string,
  params?: RequestOptions['params'],
  fallbackFilename = 'download',
  options?: Pick<RequestOptions, 'method' | 'body'>,
): Promise<void> {
  const owner = assertPageSession();
  const assertResponseSession = captureResponseSession();
  const url = buildUrl(path, params);
  const token = getAccessToken();

  const headers: Record<string, string> = { Accept: '*/*' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (owner) headers['X-Kapi-User'] = owner;
  withSucursalVentana(url, headers);

  let response: Response;
  try {
    if (options?.body !== undefined) headers['Content-Type'] = 'application/json';
    response = await fetch(url, { method: options?.method ?? 'GET', headers,
      ...(options?.body !== undefined ? { body: JSON.stringify(options.body) } : {}) });
  } catch (err) {
    assertResponseSession();
    throw new NetworkError(err);
  }

  assertResponseSession();

  if (response.status === 401) {
    if (!esPaginaLogin()) redirectToLogin();
    throw new ApiError(401, 'UNAUTHORIZED', 'Session expired — please log in again');
  }

  if (!response.ok) {
    // Export endpoints return a JSON error body on failure.
    let message = `Request failed: ${response.status} ${response.statusText}`;
    try {
      const errData = await response.json() as { error?: string; message?: string };
      avisarOtraSucursal(response.status, errData);
      message = errData?.error ?? errData?.message ?? message;
    } catch {
      // non-JSON body — keep the default message
    }
    assertResponseSession();
    throw new ApiError(response.status, 'EXPORT_ERROR', message);
  }

  // Derive filename from Content-Disposition when present.
  let filename = fallbackFilename;
  const disposition = response.headers.get('Content-Disposition');
  if (disposition) {
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    if (match?.[1]) filename = decodeURIComponent(match[1]);
  }

  const blob = await response.blob();
  assertResponseSession();
  const renewed = response.headers.get('X-Kapi-Token');
  if (renewed) storeAccessToken(renewed);
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * Open an HTML response from an authenticated endpoint in a new browser tab.
 *
 * Auth is header-based (Bearer JWT), so a plain `window.open(url)` would not
 * carry the token. This fetches the endpoint with the Authorization header,
 * reads the HTML, and opens it via a blob URL.
 *
 * @throws ApiError on non-2xx, NetworkError on network failure.
 */
export async function openHtmlPreview(
  path: string,
  params?: RequestOptions['params'],
): Promise<void> {
  const owner = assertPageSession();
  const assertResponseSession = captureResponseSession();
  const url = buildUrl(path, params);
  const token = getAccessToken();

  const headers: Record<string, string> = { Accept: 'text/html,*/*' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (owner) headers['X-Kapi-User'] = owner;
  withSucursalVentana(url, headers);

  let response: Response;
  try {
    response = await fetch(url, { method: 'GET', headers });
  } catch (err) {
    assertResponseSession();
    throw new NetworkError(err);
  }

  assertResponseSession();

  if (response.status === 401) {
    if (!esPaginaLogin()) redirectToLogin();
    throw new ApiError(401, 'UNAUTHORIZED', 'Session expired — please log in again');
  }

  if (!response.ok) {
    let message = `Request failed: ${response.status} ${response.statusText}`;
    try {
      const errData = (await response.json()) as { error?: string; message?: string };
      avisarOtraSucursal(response.status, errData);
      message = errData?.error ?? errData?.message ?? message;
    } catch {
      // non-JSON body — keep default
    }
    assertResponseSession();
    throw new ApiError(response.status, 'PREVIEW_ERROR', message);
  }

  const html = await response.text();
  assertResponseSession();
  const renewed = response.headers.get('X-Kapi-Token');
  if (renewed) storeAccessToken(renewed);
  const blob = new Blob([html], { type: 'text/html' });
  const objectUrl = window.URL.createObjectURL(blob);
  window.open(objectUrl, '_blank', 'noopener');
  // Revoke shortly after to let the new tab load.
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

// ---------------------------------------------------------------------------
// Token management helpers (used by auth.tsx)
// ---------------------------------------------------------------------------

/** La credencial pertenece a esta pestaña, incluso con clientes anteriores que pidan persistir. */
export function setAccessToken(token: string, _persistent = false): void {
  sessionGeneration++;
  permitirHerencia();
  storeAccessToken(token);
}

// Una renovación no invalida las demás peticiones paralelas de la misma sesión.
function storeAccessToken(token: string): void {
  sessionStorage.setItem('kapi:access_token', token);
  window.dispatchEvent(new Event(SESION_CAMBIADA));
  localStorage.removeItem('kapi:access_token');
}

/** Limpieza local (logout o 401); no publica un cierre entre pestañas. */
export function clearAccessToken(): void {
  impedirHerencia();
  sessionGeneration++;
  try { sessionStorage.removeItem('kapi:access_token'); } catch { /* almacenamiento no disponible */ }
  try { localStorage.removeItem('kapi:access_token'); } catch { /* no frenar la limpieza de la pestaña */ }
}
